import React, { useState, useEffect } from 'react';
import { Search, Plus, Minus, Lock, Unlock, Trash2, LogIn } from 'lucide-react';
import { API_URL } from '../../config';

export default function UsersList({ adminData, onUserClick }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortDesc, setSortDesc] = useState(true);  // NEU: true = Neueste zuerst
  const [selectedUser, setSelectedUser] = useState(null);
  const [actionModal, setActionModal] = useState(null); // 'add_snaps', 'deduct_snaps', 'block', 'unblock'
  const [snapAmount, setSnapAmount] = useState(0);
  const [reason, setReason] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  useEffect(() => {
    fetchUsers();
  }, [searchTerm]);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('snapshot_token');
      const res = await fetch(`${API_URL}/api/admin/users?search=${searchTerm}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error('Failed to fetch users');
      
      const data = await res.json();
      setUsers(data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdjustSnaps = async () => {
    if (!reason || reason.length < 10) {
      alert('Begründung muss mindestens 10 Zeichen haben!');
      return;
    }

    setIsProcessing(true);

    try {
      const token = localStorage.getItem('snapshot_token');
      const amount = actionModal === 'add_snaps' ? snapAmount : -snapAmount;
      
      const res = await fetch(`${API_URL}/api/admin/users/adjust-snaps`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user_id: selectedUser.id,
          amount: amount,
          reason: reason
        })
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Aktion fehlgeschlagen');
      }

      const data = await res.json();
      alert(`✅ SNAPs angepasst!\n\nAlt: ${data.old_snaps}\nNeu: ${data.new_snaps}\nÄnderung: ${data.change > 0 ? '+' : ''}${data.change}`);
      
      // Refresh users
      await fetchUsers();
      closeModal();
      
    } catch (error) {
      console.error('Error adjusting SNAPs:', error);
      alert(`❌ Fehler: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBlockUser = async () => {
    if (!reason || reason.length < 10) {
      alert('Begründung muss mindestens 10 Zeichen haben!');
      return;
    }

    if (!window.confirm(`User ${selectedUser.email} wirklich sperren?`)) {
      return;
    }

    setIsProcessing(true);

    try {
      const token = localStorage.getItem('snapshot_token');
      
      const res = await fetch(`${API_URL}/api/admin/users/block`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user_id: selectedUser.id,
          reason: reason
        })
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Sperren fehlgeschlagen');
      }

      alert(`✅ User ${selectedUser.email} wurde gesperrt!`);
      
      await fetchUsers();
      closeModal();
      
    } catch (error) {
      console.error('Error blocking user:', error);
      alert(`❌ Fehler: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUnblockUser = async () => {
    if (!reason || reason.length < 10) {
      alert('Begründung muss mindestens 10 Zeichen haben!');
      return;
    }

    setIsProcessing(true);

    try {
      const token = localStorage.getItem('snapshot_token');
      
      const res = await fetch(`${API_URL}/api/admin/users/unblock`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user_id: selectedUser.id,
          reason: reason
        })
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Freigabe fehlgeschlagen');
      }

      alert(`✅ User ${selectedUser.email} wurde freigegeben!`);
      
      await fetchUsers();
      closeModal();
      
    } catch (error) {
      console.error('Error unblocking user:', error);
      alert(`❌ Fehler: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleImpersonate = async (user) => {
    if (!window.confirm(`Als "${user.email}" einloggen?`)) return;
    try {
      const token = localStorage.getItem('snapshot_token');
      const res = await fetch(`${API_URL}/api/admin/impersonate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ user_id: user.id })
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Impersonation fehlgeschlagen');
      }
      const data = await res.json();
      // Übergabe via localStorage — sessionStorage ist NICHT zwischen Tabs geteilt
      // (window.open() öffnet neuen Tab mit eigener sessionStorage). Der neue Tab
      // liest den Key aus localStorage und löscht ihn sofort wieder.
      const impersonationKey = `impersonate_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      localStorage.setItem(impersonationKey, JSON.stringify({
        token: data.token,
        user: data.user,
        adminToken: localStorage.getItem('snapshot_token'),
        adminUser: localStorage.getItem('snapshot_user')
      }));
      window.open(`/welcome?impersonate=${impersonationKey}`, '_blank');
    } catch (error) {
      console.error('Error impersonating user:', error);
      alert(`❌ Fehler: ${error.message}`);
    }
  };

  const handleDeleteUser = async () => {
    if (deleteConfirmText.trim().toUpperCase() !== 'LÖSCHEN') {
      alert('Zur Bestätigung muss LÖSCHEN in Großbuchstaben exakt eingegeben werden.');
      return;
    }

    setIsProcessing(true);

    try {
      const token = localStorage.getItem('snapshot_token');
      const res = await fetch(`${API_URL}/api/auth/user/${selectedUser.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || `Löschung fehlgeschlagen (HTTP ${res.status})`);
      }

      const data = await res.json();
      const c = data.cascade || {};
      alert(
        `✅ Benutzer vollständig gelöscht\n\n${selectedUser.email}\n\n` +
        `• ${c.avatars || 0} Avatare (inkl. Astria-Tune)\n` +
        `• ${c.media || 0} Bilder\n` +
        `• ${c.video_generations || 0} Videos\n` +
        `• ${c.assembled_videos || 0} Beatleap-Videos\n` +
        `• ${c.lipsync_generations || 0} Lipsync-Videos\n` +
        `• ${c.credit_transactions || 0} Credit-Transaktionen\n` +
        `• ${c.transactions || 0} Zahlungen\n` +
        `• ${c.sessions || 0} Sessions`
      );

      await fetchUsers();
      closeModal();

    } catch (error) {
      console.error('Error deleting user:', error);
      alert(`❌ Fehler: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const openModal = (action, user) => {
    setActionModal(action);
    setSelectedUser(user);
    setReason('');
    setSnapAmount(0);
    setDeleteConfirmText('');
  };

  const closeModal = () => {
    setActionModal(null);
    setSelectedUser(null);
    setReason('');
    setSnapAmount(0);
    setDeleteConfirmText('');
  };

  return (
    <div className="p-4 md:p-6 overflow-x-hidden">
      <div className="max-w-7xl mx-auto w-full">
        
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl md:text-3xl font-bold">
              Nutzer <span className="text-[#10B981]">Verwaltung</span>
            </h2>
            <div className="text-right">
              <div className="text-2xl md:text-3xl font-bold text-[#10B981]">
                {users.length}
              </div>
              <div className="text-xs text-white/50">
                {new Date().toLocaleDateString('de-DE', { 
                  day: '2-digit', 
                  month: '2-digit', 
                  year: 'numeric' 
                })}
              </div>
            </div>
          </div>
          
          {/* Search + Sort */}
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Email oder User-ID suchen..."
                className="w-full pl-12 pr-4 py-3 bg-black border-2 border-white/30 rounded-xl text-white placeholder:text-white/40 focus:border-[#10B981] focus:ring-2 focus:ring-[#10B981]/50 transition-all"
              />
            </div>
            
            {/* Sort Toggle */}
            <button
              onClick={() => setSortDesc(!sortDesc)}
              className="px-4 py-3 bg-black border-2 border-white/30 hover:border-[#10B981] rounded-xl text-white font-semibold transition-all flex items-center gap-2 whitespace-nowrap"
              title={sortDesc ? "Neueste zuerst" : "Älteste zuerst"}
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                {sortDesc ? (
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-4-4m4 4l4-4" />
                )}
              </svg>
              <span className="hidden md:inline">{sortDesc ? '↓ Neueste' : '↑ Älteste'}</span>
            </button>
          </div>
        </div>

        {/* Users List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-12 h-12 border-4 border-[#10B981] border-t-transparent rounded-full mx-auto"></div>
          </div>
        ) : (
          <>
            {/* Desktop: Tabelle */}
            <div className="hidden md:block bg-black border-2 border-white/10 rounded-xl overflow-hidden">
              <table className="w-full">
                <thead className="bg-white/5 border-b border-white/10">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-bold text-white/70 uppercase">User-ID</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-white/70 uppercase">Email</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-white/70 uppercase">SNAPs</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-white/70 uppercase">Status</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-white/70 uppercase">Käufe</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-white/70 uppercase">Speicher</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-white/70 uppercase">Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {users
                    .sort((a, b) => {
                      const dateA = new Date(a.created_at || 0);
                      const dateB = new Date(b.created_at || 0);
                      return sortDesc ? dateB - dateA : dateA - dateB;
                    })
                    .map((user) => (
                    <tr 
                      key={user.id} 
                      onClick={() => onUserClick && onUserClick(user.id)}
                      className="border-b border-white/5 hover:bg-white/10 transition-all cursor-pointer"
                      data-testid={`user-row-${user.id}`}
                    >
                      <td className="px-6 py-4">
                        <div className="text-sm font-bold text-[#10B981]">
                          User #{user.display_id ? String(user.display_id).padStart(6, '0') : '------'}
                        </div>
                        <div className="text-xs font-mono text-white/40 mt-1">
                          {user.id.substring(0, 8)}...
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-white max-w-[220px] truncate" title={user.email || ''}>{user.email || 'N/A'}</td>
                      <td className="px-6 py-4 text-sm font-bold text-[#10B981]">{Number((user.credits || user.snaps || 0).toFixed(2))}</td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                          user.status === 'active' 
                            ? 'bg-[#10B981]/20 text-[#10B981]' 
                            : 'bg-red-500/20 text-red-500'
                        }`}>
                          {user.status || 'active'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-white">€{user.total_spent.toFixed(2)}</td>
                      <td className="px-6 py-4 text-sm text-white/70">
                        <span title={`${user.generations_count || 0} Bilder, ${user.videos_count || 0} Videos, ${user.avatars_count || 0} Avatare`}>
                          {user.storage_mb >= 1000 ? `${(user.storage_mb / 1000).toFixed(1)} GB` : `${user.storage_mb || 0} MB`}
                        </span>
                      </td>
                      <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                        <div className="flex gap-2">
                          <button
                            onClick={() => openModal('add_snaps', user)}
                            className="p-2 hover:bg-[#10B981]/20 rounded-lg transition-all"
                            title="SNAPs gutschreiben"
                          >
                            <Plus className="w-4 h-4 text-[#10B981]" />
                          </button>
                          <button
                            onClick={() => openModal('deduct_snaps', user)}
                            className="p-2 hover:bg-orange-500/20 rounded-lg transition-all"
                            title="SNAPs abziehen"
                          >
                            <Minus className="w-4 h-4 text-orange-500" />
                          </button>
                          <button
                            onClick={() => handleImpersonate(user)}
                            className="p-2 hover:bg-blue-500/20 rounded-lg transition-all"
                            title="Als User einloggen"
                          >
                            <LogIn className="w-4 h-4 text-blue-400" />
                          </button>
                          {user.status === 'active' ? (
                            <button
                              onClick={() => openModal('block', user)}
                              className="p-2 hover:bg-red-500/20 rounded-lg transition-all"
                              title="User sperren"
                            >
                              <Lock className="w-4 h-4 text-red-500" />
                            </button>
                          ) : (
                            <button
                              onClick={() => openModal('unblock', user)}
                              className="p-2 hover:bg-[#10B981]/20 rounded-lg transition-all"
                              title="User freigeben"
                            >
                              <Unlock className="w-4 h-4 text-[#10B981]" />
                            </button>
                          )}
                          <button
                            onClick={() => openModal('delete', user)}
                            className="p-2 hover:bg-red-500/20 rounded-lg transition-all"
                            title="Benutzer komplett löschen (unumkehrbar)"
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {users.length === 0 && (
                <div className="text-center py-12 text-white/50">
                  Keine Nutzer gefunden
                </div>
              )}
            </div>

            {/* Mobile: Karten */}
            <div className="md:hidden space-y-3">
              {users.map((user) => (
                <div 
                  key={user.id}
                  onClick={() => onUserClick && onUserClick(user.id)}
                  className="bg-black border-2 border-white/10 rounded-xl p-4 hover:border-[#10B981] transition-all cursor-pointer active:bg-white/5"
                  data-testid={`user-card-${user.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="text-base font-bold text-[#10B981] mb-1">
                        User #{user.display_id ? String(user.display_id).padStart(6, '0') : '------'}
                      </div>
                      <div className="text-sm text-white/80 break-all">
                        {user.email || 'N/A'}
                      </div>
                    </div>
                    <svg className="w-5 h-5 text-white/40 flex-shrink-0 ml-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              ))}
              
              {users.length === 0 && (
                <div className="text-center py-12 text-white/50 bg-black border-2 border-white/10 rounded-xl">
                  Keine Nutzer gefunden
                </div>
              )}
            </div>
          </>
        )}

        {/* Action Modal */}
        {actionModal && selectedUser && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
            <div className="w-full max-w-md bg-black border-2 border-[#10B981]/30 rounded-xl p-6">
              
              <h3 className="text-xl font-bold mb-4">
                {actionModal === 'add_snaps' && '➕ SNAPs gutschreiben'}
                {actionModal === 'deduct_snaps' && '➖ SNAPs abziehen'}
                {actionModal === 'block' && '🔒 User sperren'}
                {actionModal === 'unblock' && '🔓 User freigeben'}
                {actionModal === 'delete' && '🗑️ Benutzer komplett löschen'}
              </h3>

              <p className="text-white/70 text-sm mb-4">
                User: <span className="text-[#10B981] font-bold">{selectedUser.email}</span>
              </p>

              {(actionModal === 'add_snaps' || actionModal === 'deduct_snaps') && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-white/70 mb-2">
                    Anzahl SNAPs
                  </label>
                  <input
                    type="number"
                    value={snapAmount}
                    onChange={(e) => setSnapAmount(parseInt(e.target.value) || 0)}
                    min="0"
                    className="w-full px-4 py-3 bg-black border-2 border-white/30 rounded-xl text-white focus:border-[#10B981] transition-all"
                  />
                </div>
              )}

              {actionModal === 'delete' && (
                <>
                  <div className="mb-4 p-4 bg-red-950/40 border-2 border-red-500/50 rounded-xl">
                    <p className="text-red-400 font-bold mb-2">⚠️ UNUMKEHRBARE LÖSCHUNG</p>
                    <p className="text-sm text-white/80 mb-2">Folgendes wird dauerhaft entfernt:</p>
                    <ul className="text-sm text-white/70 space-y-1 ml-5 list-disc">
                      <li>Alle SNAPs-Credits des Benutzers</li>
                      <li>Alle generierten Bilder und Videos (Datenbank + Dateien)</li>
                      <li>Alle Avatare <span className="font-semibold text-red-300">inklusive Löschung bei Astria.ai</span></li>
                      <li>Kauf-Historie, Einwilligungen, Referrals, Community-Beiträge</li>
                      <li>Der Benutzer-Account selbst</li>
                    </ul>
                  </div>
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-white/70 mb-2">
                      Zur Bestätigung <span className="font-bold text-red-400">LÖSCHEN</span> in Großbuchstaben eintippen:
                    </label>
                    <input
                      type="text"
                      value={deleteConfirmText}
                      onChange={(e) => setDeleteConfirmText(e.target.value)}
                      placeholder="LÖSCHEN"
                      autoComplete="off"
                      className="w-full px-4 py-3 bg-black border-2 border-red-500/50 rounded-xl text-white placeholder:text-white/30 focus:border-red-500 transition-all text-center font-bold text-lg tracking-[0.25em]"
                    />
                  </div>
                </>
              )}

              {actionModal !== 'delete' && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-white/70 mb-2">
                    Begründung (mind. 10 Zeichen)
                  </label>
                  <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Warum wird diese Aktion durchgeführt?"
                    rows={3}
                    className="w-full px-4 py-3 bg-black border-2 border-white/30 rounded-xl text-white placeholder:text-white/40 focus:border-[#10B981] transition-all resize-none"
                  />
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={closeModal}
                  disabled={isProcessing}
                  className="flex-1 py-3 px-6 border-2 border-white/30 text-white rounded-xl hover:bg-white/5 transition-all"
                >
                  Abbrechen
                </button>
                <button
                  onClick={() => {
                    if (actionModal === 'add_snaps' || actionModal === 'deduct_snaps') {
                      handleAdjustSnaps();
                    } else if (actionModal === 'block') {
                      handleBlockUser();
                    } else if (actionModal === 'unblock') {
                      handleUnblockUser();
                    } else if (actionModal === 'delete') {
                      handleDeleteUser();
                    }
                  }}
                  disabled={
                    isProcessing ||
                    (actionModal === 'delete'
                      ? deleteConfirmText.trim().toUpperCase() !== 'LÖSCHEN'
                      : (!reason || reason.length < 10))
                  }
                  className={
                    actionModal === 'delete'
                      ? 'flex-1 py-3 px-6 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-all font-bold disabled:opacity-40 disabled:cursor-not-allowed'
                      : 'flex-1 py-3 px-6 bg-[#10B981] text-black rounded-xl hover:bg-[#059669] transition-all font-bold disabled:opacity-50'
                  }
                >
                  {isProcessing
                    ? 'Verarbeite...'
                    : actionModal === 'delete'
                      ? 'Endgültig löschen'
                      : 'Bestätigen'}
                </button>
              </div>

            </div>
          </div>
        )}

      </div>
    </div>
  );
}
